var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { DateFormatter, DexfSettingsUtility, PriceUtility, TotalPriceCalculator, UrlUtility } from '@inter-ikea-kompis/utilities';
import { ConfigurationSummaryWeightUtility } from '@inter-ikea-kompis/component-configuration-summary';
import { PriceFormatter } from '@inter-ikea-kompis/component-price-formatter';
import { QrCode } from '@inter-ikea-kompis/component-qr-code';
import { DesignSourceEnum } from '@insights/insights-data-provider';
import SummaryPageConfigurationMeasureEnum from '../../enums/SummaryPageConfigurationMeasureEnum.js';
import SummaryPageBrowserUtility from '../../utilities/SummaryPageBrowserUtility.js';
import SummaryPageMeasurementUtility from '../../utilities/SummaryPageMeasurementUtility.js';
import IkeaLogo from '../summary-page-header/IkeaLogo.js';
import SummaryPagePrintStyle from './SummaryPagePrintStyle.js';
/**
 * The printview for the Summary Page.
 *
 * @event qrCodeRendered Triggered when the QR code has finished rendering.
 */
export default class SummaryPagePrint extends KompisElement {
    constructor() {
        super(...arguments);
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The settings retrieved from settings service.
        this.settings = null;
        // Controls how measurements should be shown in the configuration summary.
        this.configurationMeasures = SummaryPageConfigurationMeasureEnum.short;
        // The url for the screenshot.
        this.screenshotUrl = null;
        // The alt text for the screenshot. If not set, a default alt text will be used.
        this.screenshotAlt = null;
        // The name of the product, e.g. PLATSA. If set is transformed to uppercase and used in the title of the printed page.
        this.productName = null;
        // VPC code for the solution.
        this.vpcCode = null;
        // Measurements of height, width and depth in millimeter. Can be left empty to hide dimensions.
        this.measurements = null;
        // Used as a link for the QR code. When copied, query parameter with vpc source is added.
        this.designLink = null;
        // Shows total package weight of the shopping products.
        this.showTotalPackageWeight = false;
        // A list of included products within SPR products. Must be set when using layout compact print and printing SPR's. Must also be set (if applicable) for total package weight.
        this.includedProducts = null;
        // Name of the store to show in the page footer
        this.storeName = null;
        // Retail unit to show in the page footer
        this.retailUnit = null;
        // Override market setting "useMetricMeasures" by controlling the measurement system in the planner.
        this.measurementSystem = null;
        this.dexfSettings = null;
        this.listeners = {
            beforeprint: this.onBeforePrint.bind(this),
            afterprint: this.onAfterPrint.bind(this)
        };
        this.printStyleTag = null;
    }
    render() {
        // We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslationsAndSettings()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        return html `
			<style>
				${SummaryPagePrintStyle.getStyle({
            isSafari: SummaryPageBrowserUtility.isSafari(),
            includesScreenshot: !!this.screenshotUrl
        })}
			</style>
			${this.getCopyrightNotice()} ${this.getFooterText()} ${this.getFooterLogo()}
			${this.getPrintHeader()}
		`;
    }
    connectedCallback() {
        this.customElements.define('kompis-qr-code', QrCode);
        this.customElements.define('kompis-price-formatter', PriceFormatter);
        super.connectedCallback();
        window.addEventListener('beforeprint', this.listeners.beforeprint);
        window.addEventListener('afterprint', this.listeners.afterprint);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener('beforeprint', this.listeners.beforeprint);
        window.removeEventListener('afterprint', this.listeners.afterprint);
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('localizedInformation') &&
            this.localizedInformation &&
            !this.settings) {
            this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
        }
        if (changedProperties.has('settings') && this.settings) {
            this.dexfSettings = this.settings;
        }
        return true;
    }
    hasTranslationsAndSettings() {
        return (!!this.translations && !!this.settings) || !!this.localizedInformation;
    }
    getTranslations() {
        return this.translations
            ? this.translations
            : this.localizedInformation?.translations;
    }
    getSettings() {
        return this.dexfSettings;
    }
    onBeforePrint() {
        // For future developers:
        // Anything here must be done synchronously for it to show up in the print
        // view. That means no promises or callbacks. You can modify the content of
        // the component and even re-render it but when you've done your state
        // changes you must call `this.update()` and not `this.requestUpdate()`
        // since the latter returns a promise which you have to await if you want to
        // be sure the update has been applied.
        this.injectPrintCss();
    }
    onAfterPrint() {
        this.removePrintCss();
    }
    injectPrintCss() {
        this.printStyleTag = document.createElement('style');
        this.printStyleTag.textContent = SummaryPagePrintStyle.getPrintStyle();
        document.head.appendChild(this.printStyleTag);
    }
    removePrintCss() {
        this.printStyleTag?.remove();
        this.printStyleTag = null;
    }
    getCopyrightNotice() {
        const year = new Date().getFullYear().toString();
        const text = this.getTranslations().copyrightNotice.replace('{{year}}', year);
        return html `<div class="copyright-notice">${text}</div>`;
    }
    getFooterText() {
        const retailUnit = this.retailUnit?.toUpperCase() ?? '';
        let text = '';
        if (this.storeName && retailUnit) {
            text = `${this.storeName} (${retailUnit})`;
        }
        else if (retailUnit) {
            text = retailUnit;
        }
        else if (this.storeName) {
            text = this.storeName;
        }
        if (text) {
            text += ', ';
        }
        return html `<div class="footer-text">${text}${this.getDateString()}</div>`;
    }
    getDateString() {
        return DateFormatter.dateToString(new Date(), this.getSettings().localisation.dateFormat + ', HH:mm');
    }
    getFooterLogo() {
        const footerLogoDiv = document.createElement('div');
        footerLogoDiv.classList.add('footer-logo');
        footerLogoDiv.innerHTML = IkeaLogo;
        return footerLogoDiv;
    }
    getPrintHeader() {
        // Double spaces left by supplying no product name will be collapsed by the browser.
        const titleText = this.getTranslations().summaryPagePrintTitle.replace('{{product_name}}', this.productName?.toUpperCase() ?? '');
        return html `
			<div class="print-header">
				<h1 class="title">${titleText}</h1>
				<div class="print-details">
					<div>
						${this.getMeasurements()}${this.getTotalPackageWeight()}
						${this.getCompactPrice()}${this.getDesignCodeInfo()}
					</div>

					${this.getQrCode()}
				</div>

				${this.getScreenshot()}
			</div>
		`;
    }
    getCompactPrice() {
        const { priceInfoIkeaFamilyPrice, priceInfoRegularPrice } = this.getTranslations();
        const totalPrice = TotalPriceCalculator.calculate(this.shoppingProducts, this.getSettings().localisation.dateFormat);
        const listTotalPrice = PriceUtility.getPrice(totalPrice);
        return html `
			<div class="compactPriceWrapper">
				${totalPrice.family
            ? html `
							<p class="compactCommercialMessage text--heading-m">${priceInfoIkeaFamilyPrice}</p>
						`
            : ''}
				<p class="compactPrice text--heading-l">
					<kompis-price-formatter
						.settings="${this.settings}"
						.localizedInformation="${this.localizedInformation}"
						.amount="${totalPrice.family ? listTotalPrice : totalPrice.inclTax}"
					></kompis-price-formatter>
				</p>
				${totalPrice.family
            ? html `
							<p class="compactCommercialMessage text--heading-s">${priceInfoRegularPrice}</p>
							<p class="compactPrice text--heading-m">
								<kompis-price-formatter
									.settings="${this.settings}"
									.localizedInformation="${this.localizedInformation}"
									.amount="${totalPrice.inclTax}"
								></kompis-price-formatter>
							</p>
						`
            : ''}
			</div>
		`;
    }
    getDesignCodeInfo() {
        if (!this.vpcCode) {
            return null;
        }
        return html `
			<div class="designCodeInfo">
				${this.getTranslations().designCodeTitle} <b>${this.vpcCode}</b>
			</div>
		`;
    }
    /**
     * Get Measurements.
     * Short or long format depending on prop. Unit is cm/in depending on localizedInformation.
     */
    getMeasurements() {
        if (!this.measurements) {
            return null;
        }
        const useLongFormat = this.configurationMeasures === SummaryPageConfigurationMeasureEnum.long;
        const measurementRows = SummaryPageMeasurementUtility.getLocalizedMeasurementRows(this.getTranslations(), this.getSettings(), this.locale, this.measurements, useLongFormat, this.measurementSystem);
        return html `
			<span class="measurements text--body-m">
				${measurementRows.map((row) => html `${row}<br />`)}
			</span>
		`;
    }
    getTotalPackageWeight() {
        if (!this.showTotalPackageWeight || !this.locale) {
            return null;
        }
        const totalPackageWeightKgs = ConfigurationSummaryWeightUtility.getTotalWeightInKg(this.shoppingProducts, this.includedProducts);
        const localizedWeight = ConfigurationSummaryWeightUtility.getLocalizedWeight(totalPackageWeightKgs, this.locale, this.getTranslations(), this.getSettings().localisation.useMetricMeasures, this.measurementSystem);
        const localizedWeightText = ConfigurationSummaryWeightUtility.getLocalizedWeightHtmlString(localizedWeight);
        return html `
			<span class="totalPackageWeight text--body-m">
				${this.getTranslations().configurationSummaryTotalPackageWeight}: ${localizedWeightText}
			</span>
		`;
    }
    getScreenshot() {
        // Don't use Kompis image! It loads the image asynchronously and that
        // doesn't work consistently with print.
        return this.screenshotUrl
            ? html `<img
					class="screenshot"
					alt="${this.screenshotAlt ?? this.getTranslations().summaryPageScreenshotAltText}"
					src="${this.screenshotUrl}"
				/>`
            : null;
    }
    getQrCode() {
        if (!this.designLink) {
            return null;
        }
        return html `
			<div class="qr-code">
				<kompis-qr-code
					.url="${UrlUtility.addQueryParameter(this.designLink, 'vpcSource', DesignSourceEnum.qr)}"
					.qrCodeTitle="${this.getTranslations().summaryPageQrCodeTitle}"
					@renderedToDom="${() => this.dispatchEvent(new CustomEvent('qrCodeRendered', { bubbles: true }))}"
					;
				></kompis-qr-code>
				<p class="qr-code-text text--body-m">${this.getTranslations().summaryPageQrCodeText}</p>
			</div>
		`;
    }
}
__decorate([
    property({ type: Object })
], SummaryPagePrint.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], SummaryPagePrint.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], SummaryPagePrint.prototype, "settings", void 0);
__decorate([
    property({ type: String, required: true })
], SummaryPagePrint.prototype, "locale", void 0);
__decorate([
    property({ type: Array, required: true })
], SummaryPagePrint.prototype, "shoppingProducts", void 0);
__decorate([
    property({ type: String })
], SummaryPagePrint.prototype, "configurationMeasures", void 0);
__decorate([
    property({ type: String })
], SummaryPagePrint.prototype, "screenshotUrl", void 0);
__decorate([
    property({ type: String })
], SummaryPagePrint.prototype, "screenshotAlt", void 0);
__decorate([
    property({ type: String })
], SummaryPagePrint.prototype, "productName", void 0);
__decorate([
    property({ type: String })
], SummaryPagePrint.prototype, "vpcCode", void 0);
__decorate([
    property({ type: Object })
], SummaryPagePrint.prototype, "measurements", void 0);
__decorate([
    property({ type: String })
], SummaryPagePrint.prototype, "designLink", void 0);
__decorate([
    property({ type: Boolean })
], SummaryPagePrint.prototype, "showTotalPackageWeight", void 0);
__decorate([
    property({ type: Array })
], SummaryPagePrint.prototype, "includedProducts", void 0);
__decorate([
    property({ type: String })
], SummaryPagePrint.prototype, "storeName", void 0);
__decorate([
    property({ type: String })
], SummaryPagePrint.prototype, "retailUnit", void 0);
__decorate([
    property({ type: String })
], SummaryPagePrint.prototype, "measurementSystem", void 0);
//# sourceMappingURL=SummaryPagePrint.js.map